import { PlanResponse } from '@/planList/domain/PlanResponse';
import { GetDentalPlanUseCase } from '@/planList/application/GetDentalPlanUseCase';
import { GetVisionPlanUseCase } from '@/planList/application/GetVisionPlanUseCase';
import { DentalPlansApi } from '@/planList/infrastructure/dataSource/DentalPlansApi';
import { VisionPlansApi } from '@/planList/infrastructure/dataSource/VisionPlansApi';
import { Quote } from '@/application/shared/domain/Quote';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { SfApiResponse, Ploc, assertNever } from '@hfc/ui-core';

const dentalPlansApi = new DentalPlansApi();
const getDentalPlanList = new GetDentalPlanUseCase(dentalPlansApi);

const visionPlansApi = new VisionPlansApi();
const getVisionPlanList = new GetVisionPlanUseCase(visionPlansApi);

export interface PlanStoreInterface {
	plans: Array<PlanResponse>;
	loadingPlans: boolean;
  selectedVisionPlan: PlanResponse;
  selectedDentalPlan: PlanResponse;
}

const initialState: PlanStoreInterface = {
  plans: [] as Array<PlanResponse>,
  loadingPlans: false,
  selectedVisionPlan: {} as PlanResponse,
  selectedDentalPlan: {} as PlanResponse,
};

export class PlanStore extends Ploc<PlanStoreInterface> {

  public constructor () {
    super(initialState);
    this.initPlanSelected();
  }

  private initPlanSelected (): void {
    const dentalPlan = JSON.parse(localStorage.getItem('dentalPlan') || '{}') as PlanResponse;
    this.changeState({ ...this.state, selectedDentalPlan: dentalPlan });

    const visionPlan = JSON.parse(localStorage.getItem('visionPlan') || '{}') as PlanResponse;
    this.changeState({ ...this.state, selectedVisionPlan: visionPlan });
  }

  public async updateSelectedPlan (newPlan: PlanResponse, applicationType: APPLICATION_TYPES): Promise<void> {
    this.changeState({ ...this.state, loadingPlans: true });

    switch (applicationType) {
      case APPLICATION_TYPES.DENTAL:
        this.changeState({ ...this.state, selectedDentalPlan: newPlan });
        break;
      case APPLICATION_TYPES.VISION:
        this.changeState({ ...this.state, selectedVisionPlan: newPlan });
        break;
      case APPLICATION_TYPES.ADD_ON_DENTAL:
      case APPLICATION_TYPES.ADD_ON_VISION:
      case APPLICATION_TYPES.DELEGATION:
      case APPLICATION_TYPES.HEALTH:
      case APPLICATION_TYPES.HEALTH_TEMP:
      case APPLICATION_TYPES.MEDICAL:
      case APPLICATION_TYPES.MEDICAL_TRANSITIONER:
      case APPLICATION_TYPES.MEDICARE:
      case APPLICATION_TYPES.SMALL_BUSINESS:
        break;
      default: assertNever(applicationType);
    }

    localStorage.setItem(`${applicationType}Plan`, JSON.stringify(newPlan));

    this.changeState({ ...this.state, loadingPlans: false });
  }

  public async getDentalPlanList (quoteData: Quote): Promise<SfApiResponse> {
    return this.getPlanList(getDentalPlanList, quoteData);
  }

  public async getVisionPlanList (quoteData: Quote): Promise<SfApiResponse> {
    return this.getPlanList(getVisionPlanList, quoteData);
  }

  public async getPlanList (getPlanUseCase: GetDentalPlanUseCase | GetVisionPlanUseCase, quoteData: Quote): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loadingPlans: true });
    const result = await getPlanUseCase.run(quoteData);
    this.changeState({ ...this.state, loadingPlans: false });

    if (result.success && result.data) {
      this.changeState({ ...this.state, plans: result.data });
    }

    return result;
  }

  public async getQuoteData (applicationType: APPLICATION_TYPES): Promise<Quote> {
    this.changeState({ ...this.state, loadingPlans: true });

    const result = JSON.parse(localStorage.getItem(applicationType === APPLICATION_TYPES.DENTAL ? 'dentalQuote' : 'visionQuote') || '{}') as Quote;

    this.changeState({ ...this.state, loadingPlans: false });

    return result;
  }

  public async resetLocalStorage (applicationType: APPLICATION_TYPES): Promise<void> {
    this.changeState({ ...this.state, selectedDentalPlan: {} as PlanResponse, selectedVisionPlan: {} as PlanResponse, plans: [] as Array<PlanResponse>, loadingPlans: false });
    localStorage.removeItem(`${applicationType}Plan`);
  }

}
